/**
 * App entry point. Order of wrappers (outermost first):
 *   StrictMode → Redux Provider → AppInitWrapper (Katal init) →
 *   MidwayAuthGate → Suspense → App
 * The Redux Provider must wrap everything that calls useAppSelector /
 * useAppDispatch / RTK Query hooks — i.e. the whole app. MidwayAuthGate holds
 * rendering until the Midway session flow resolves (spec midway-client-auth),
 * so nothing renders — and no query mounts — unauthenticated on deployed
 * stages.
 */

import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import Spinner from '@amzn/awsui-components-core/spinner';

import { store } from './app/store';
import { prefetchBootstrapQueries } from './app/bootstrapPrefetch';
import App from './components/App';
import AppInitWrapper from './components/AppInitWrapper';
import { MidwayAuthGate } from './components/layout/MidwayAuthGate';
import { ensureMidwaySession } from './accessors/midwayAuth';
import './monitoring';

import '@amzn/awsui-global-styles/polaris.css';
import './index.scss';

// EARLY-FIRE: start the independent bootstrap API calls NOW, before React
// mounts — they overlap the render pipeline instead of following it. See
// bootstrapPrefetch.ts for the dedupe/keep-alive contract. Deliberately NOT
// gated on the Midway session check: the API authenticates via the Midway
// cookie (SentryFetch/MCS), not the id_token, so with a live session these
// calls succeed in parallel with the mint. Only a dead session wastes them —
// and that page is about to redirect to login anyway.
prefetchBootstrapQueries(window.location.pathname);

// Kick the Midway session flow immediately as well (memoized — the gate
// awaits the same promise), so the mint and the prefetch run concurrently.
// The catch keeps a rejection from surfacing unhandled at THIS call site;
// the gate handles its own copy (fail-open, R1.5).
ensureMidwaySession().catch(() => undefined);

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <StrictMode>
    <Provider store={store}>
      <AppInitWrapper>
        <MidwayAuthGate>
          <Suspense fallback={<Spinner size="large" />}>
            <App />
          </Suspense>
        </MidwayAuthGate>
      </AppInitWrapper>
    </Provider>
  </StrictMode>,
);
